import React from "react";
import {
  ResponsiveContainer,
  ComposedChart,
  BarChart,
  LineChart,
  AreaChart,
  PieChart,
  Bar,
  Line,
  Area,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  LabelList,
} from "recharts";

import { Strings } from "../../../resources/i18n/i18n";
import {
  ANALYTICS_CHART_PALETTES,
  ANALYTICS_CHART_ORDINAL,
  ANALYTICS_CHART_INK,
  ANALYTICS_CHART_TYPES,
} from "../../../constants/appConstants";
import "./AnalyticsChart.scss";

class AnalyticsChart extends React.Component {
  colorFor = (index, count) => {
    if (this.props.ordinal) {
      return this.ordinalColorFor(index, count);
    }
    const palette =
      ANALYTICS_CHART_PALETTES[this.props.palette] ||
      ANALYTICS_CHART_PALETTES.default;
    return index < palette.length ? palette[index] : ANALYTICS_CHART_INK.folded;
  };


  ordinalColorFor = (index, count) => {
    const sizes = Object.keys(ANALYTICS_CHART_ORDINAL)
      .map(Number)
      .sort((a, b) => a - b);
    const chosen = sizes.find((size) => size >= (count || 1)) ?? sizes.at(-1);
    const ramp = ANALYTICS_CHART_ORDINAL[chosen];
    if ((count || 1) <= 1) {
      return ramp[ramp.length - 1];
    }
    const step = Math.round((index * (ramp.length - 1)) / (count - 1));
    return ramp[Math.min(step, ramp.length - 1)];
  };

  formatValue = (value) => {
    if (value === null || value === undefined) {
      return "";
    }
    const number = Number(value);
    if (Number.isNaN(number)) {
      return String(value);
    }
    return this.props.money
      ? number.toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      : number.toLocaleString();
  };
  formatAxisTick = (value) => {
    const number = Number(value);
    if (Number.isNaN(number)) {
      return String(value);
    }
    if (Math.abs(number) >= 1000000) {
      return (number / 1000000).toFixed(1) + "M";
    }
    if (Math.abs(number) >= 1000) {
      return (number / 1000).toFixed(1) + "k";
    }
    return String(number);
  };

  renderLegendLabel = (value) => (
    <span className="analyticsChartLegendLabel">{value}</span>
  );

  commonAxes = (horizontal) => {
    const categoryAxis = (
      <XAxis
        type={horizontal ? "number" : "category"}
        dataKey={horizontal ? undefined : this.props.xKey || "month"}
        tick={{ fill: ANALYTICS_CHART_INK.muted, fontSize: 11 }}
        tickFormatter={horizontal ? this.formatAxisTick : undefined}
        stroke={ANALYTICS_CHART_INK.axis}
        interval="preserveStartEnd"
        minTickGap={8}
      />
    );
    const valueAxis = (
      <YAxis
        type={horizontal ? "category" : "number"}
        dataKey={horizontal ? this.props.xKey || "month" : undefined}
        tick={{ fill: ANALYTICS_CHART_INK.muted, fontSize: 11 }}
        tickFormatter={horizontal ? undefined : this.formatAxisTick}
        stroke={ANALYTICS_CHART_INK.axis}
        width={horizontal ? 150 : 52}
      />
    );
    return [categoryAxis, valueAxis];
  };

  renderCartesian = () => {
    const { type, data, series, height } = this.props;
    const horizontal = type === ANALYTICS_CHART_TYPES.HORIZONTAL_BAR;
    const stacked =
      type === ANALYTICS_CHART_TYPES.STACKED_BAR ||
      type === ANALYTICS_CHART_TYPES.STACKED_AREA;
    const [categoryAxis, valueAxis] = this.commonAxes(horizontal);
    const showLegend = series.length > 1;

    const marks = series.map((one, index) => {
      const color = this.colorFor(index, series.length);
      if (type === ANALYTICS_CHART_TYPES.LINE) {
        return (
          <Line
            key={one.key}
            type="monotone"
            dataKey={one.key}
            name={one.label}
            stroke={color}
            strokeWidth={2}
            dot={{ r: 2.5, strokeWidth: 0, fill: color }}
            activeDot={{ r: 4.5, stroke: ANALYTICS_CHART_INK.surface, strokeWidth: 2 }}
          />
        );
      }
      if (
        type === ANALYTICS_CHART_TYPES.AREA ||
        type === ANALYTICS_CHART_TYPES.STACKED_AREA
      ) {
        return (
          <Area
            key={one.key}
            type="monotone"
            dataKey={one.key}
            name={one.label}
            stackId={stacked ? "a" : undefined}
            stroke={color}
            strokeWidth={2}
            fill={color}
            fillOpacity={stacked ? 0.85 : 0.18}
          />
        );
      }
      return (
        <Bar
          key={one.key}
          dataKey={one.key}
          name={one.label}
          stackId={stacked ? "a" : undefined}
          fill={color}
          stroke={ANALYTICS_CHART_INK.surface}
          strokeWidth={stacked ? 1 : 0}
          radius={stacked ? 0 : [3, 3, 0, 0]}
	          maxBarSize={28}
        >
          {this.props.ordinal &&
            series.length === 1 &&
            data.map((row, rowIndex) => (
              <Cell
                key={"ordinal" + rowIndex}
                fill={this.colorFor(rowIndex, data.length)}
              />
            ))}
          {series.length === 1 && !horizontal && (
            <LabelList
              dataKey={one.key}
              position="top"
              formatter={this.formatValue}
              style={{ fill: ANALYTICS_CHART_INK.secondary, fontSize: 10 }}
            />
          )}
        </Bar>
      );
    });

    const ChartType =
      type === ANALYTICS_CHART_TYPES.LINE
        ? LineChart
        : type === ANALYTICS_CHART_TYPES.AREA ||
          type === ANALYTICS_CHART_TYPES.STACKED_AREA
        ? AreaChart
        : type === ANALYTICS_CHART_TYPES.BAR ||
          type === ANALYTICS_CHART_TYPES.STACKED_BAR ||
          horizontal
        ? BarChart
        : ComposedChart;

    return (
      <ResponsiveContainer width="100%" height={height || 300}>
        <ChartType
          data={data}
          layout={horizontal ? "vertical" : "horizontal"}
          margin={{ top: 12, right: 16, bottom: 4, left: 4 }}
        >
          <CartesianGrid
            stroke={ANALYTICS_CHART_INK.grid}
            strokeWidth={1}
            vertical={horizontal}
            horizontal={!horizontal}
          />
          {categoryAxis}
          {valueAxis}
          <Tooltip
            formatter={this.formatValue}
            contentStyle={{
              borderRadius: 6,
              border: "1px solid " + ANALYTICS_CHART_INK.grid,
              fontSize: 12,
            }}
          />
          {showLegend && (
            <Legend
              iconType="circle"
              iconSize={9}
              formatter={this.renderLegendLabel}
              wrapperStyle={{ fontSize: 12, paddingTop: 6 }}
            />
          )}
          {marks}
        </ChartType>
      </ResponsiveContainer>
    );
  };

  renderPie = () => {
    const { data, series, height, xKey } = this.props;
    const valueKey = series[0].key;
    const nameKey = xKey || "month";
    const limit = this.props.maxSlices || 6;

    const sorted = [...data].sort(
      (a, b) => Number(b[valueKey] || 0) - Number(a[valueKey] || 0)
    );
    let slices = sorted;
    if (sorted.length > limit) {
      const head = sorted.slice(0, limit - 1);
      const tail = sorted.slice(limit - 1);
      const other = tail.reduce(
        (sum, row) => sum + Number(row[valueKey] || 0),
        0
      );
      slices = [
        ...head,
        { [nameKey]: Strings("analyticsChartOther"), [valueKey]: other },
      ];
    }

    return (
      <ResponsiveContainer width="100%" height={height || 300}>
        <PieChart margin={{ top: 8, right: 8, bottom: 8, left: 8 }}>
          <Pie
            data={slices}
            dataKey={valueKey}
            nameKey={nameKey}
            innerRadius="45%"
            outerRadius="78%"
            paddingAngle={2}
            stroke={ANALYTICS_CHART_INK.surface}
            strokeWidth={2}
          >
            {slices.map((slice, index) => (
              <Cell
                key={String(slice[nameKey]) + index}
                fill={this.colorFor(index, slices.length)}
              />
            ))}
          </Pie>
          <Tooltip formatter={this.formatValue} contentStyle={{ fontSize: 12 }} />
          <Legend
            iconType="circle"
            iconSize={9}
            formatter={this.renderLegendLabel}
            wrapperStyle={{ fontSize: 12 }}
          />
        </PieChart>
      </ResponsiveContainer>
    );
  };

  render() {
    const { data, series, title, subtitle, type } = this.props;
    const hasData =
      Array.isArray(data) &&
      data.length > 0 &&
      Array.isArray(series) &&
      series.length > 0;

    return (
      <div className="analyticsChart">
        <div className="analyticsChartHeader">
          <div className="analyticsChartHeading">
            {title && <div className="analyticsChartTitle">{title}</div>}
            {subtitle && <div className="analyticsChartSubtitle">{subtitle}</div>}
          </div>
          {this.props.actions && (
            <div className="analyticsChartActions">{this.props.actions}</div>
          )}
        </div>
        {hasData ? (
          type === ANALYTICS_CHART_TYPES.PIE ? (
            this.renderPie()
          ) : (
            this.renderCartesian()
          )
        ) : (
          <div className="analyticsChartEmpty">
            {Strings("analyticsChartNoData")}
          </div>
        )}
      </div>
    );
  }
}

export default AnalyticsChart;
